import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import api, { BACKEND_URL } from '../../api/axios';

const STATUS_META = {
  pending_verification: { label: 'Pending Verification', cls: 'bg-gray-100 text-gray-600' },
  documents_submitted: { label: 'Documents Submitted', cls: 'bg-[#FFFBEB] text-[#F59E0B]' },
  under_review: { label: 'Under Review', cls: 'bg-[#EFF6FF] text-[#2563EB]' },
  verified: { label: 'Verified', cls: 'bg-[#ECFDF5] text-[#10B981]' },
  rejected: { label: 'Rejected', cls: 'bg-[#FEF2F2] text-[#EF4444]' },
  resubmit_required: { label: 'Resubmit Required', cls: 'bg-[#FEF2F2] text-[#EF4444]' },
};

const DOC_LABELS = {
  gst: 'GST Certificate',
  cin: 'Certificate of Incorporation',
  udyam: 'Udyam / Udyog Aadhaar',
  pan: 'PAN Card',
  aadhaar: 'Personal Aadhaar',
  license: 'Company License',
};

const DOC_STATUS_META = {
  pending: { label: 'Pending Review', cls: 'bg-[#FFFBEB] text-[#F59E0B]' },
  approved: { label: 'Approved', cls: 'bg-[#ECFDF5] text-[#10B981]' },
  rejected: { label: 'Rejected', cls: 'bg-[#FEF2F2] text-[#EF4444]' },
  resubmission_requested: { label: 'Resubmission Requested', cls: 'bg-[#FEF2F2] text-[#EF4444]' },
};

const ACTION_LABELS = {
  document_uploaded: 'Document uploaded',
  document_approved: 'Document approved',
  document_rejected: 'Document rejected',
  resubmission_requested: 'Resubmission requested',
  review_started: 'Marked under review',
  email_verified: 'Official email verified',
  employer_verified: 'Employer verified',
  employer_rejected: 'Employer rejected',
  status_changed: 'Status changed',
};

// Fields shown in the "Edit Details" form. label = display, key = employer field name.
const EDIT_FIELDS = [
  { key: 'name', label: 'Company / Contact Name' },
  { key: 'email', label: 'Email', type: 'email' },
  { key: 'phone', label: 'Phone' },
  { key: 'city', label: 'City' },
  { key: 'pincode', label: 'Pincode' },
  { key: 'address', label: 'Address', textarea: true },
  { key: 'company_industry', label: 'Industry' },
  { key: 'company_size', label: 'Company Size' },
  { key: 'company_website', label: 'Website' },
  { key: 'company_gst', label: 'Company GST' },
  { key: 'company_about', label: 'About Company', textarea: true },
];

function resolveMediaUrl(path) {
  if (!path) return null;
  return path.startsWith('http') ? path : `${BACKEND_URL}/storage/${path}`;
}

function fmtDateTime(d) {
  if (!d) return '\u2014';
  return new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
}

function CityAutocompleteInput({ value, onChange, hasError }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const q = value.trim();
    if (q.length < 2) {
      setSuggestions([]);
      return;
    }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.get('/ai/suggest-cities', { params: { query: q } });
        setSuggestions(res.data?.suggestions || []);
      } catch (err) {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 350); // debounce so we don't hit the AI endpoint on every keystroke

    return () => clearTimeout(timer);
  }, [value]);

  return (
    <div className="relative">
      <input
        type="text"
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)} // allow click on option before closing
        autoComplete="off"
        className={`w-full border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent ${hasError ? 'border-red-400' : 'border-gray-200'}`}
      />
      {open && (loading || suggestions.length > 0) && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {loading && (
            <p className="px-3 py-2 text-xs text-gray-400">Loading&hellip;</p>
          )}
          {!loading && suggestions.map((s) => (
            <button
              key={s}
              type="button"
              onMouseDown={(e) => e.preventDefault()} // prevent input blur before click registers
              onClick={() => { onChange(s.split(',')[0].trim()); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-[#F5F3FF]"
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Edit Employer Details modal                                             */
/* ---------------------------------------------------------------------- */

function EditEmployerModal({ apiPrefix, employer, onClose, onSaved }) {
  const [form, setForm] = useState(() =>
    EDIT_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: employer[f.key] || '' }), {})
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setFieldErrors({});
    try {
      await api.put(`${apiPrefix}/employers/${employer.id}`, form);
      onSaved();
      onClose();
    } catch (err) {
      const resErrors = err.response?.data?.errors;
      if (resErrors) {
        // Flatten Laravel's { field: ["msg"] } shape into { field: "msg" }
        setFieldErrors(Object.fromEntries(Object.entries(resErrors).map(([k, v]) => [k, v[0]])));
        setError('Please fix the highlighted fields.');
      } else {
        setError(err.response?.data?.message || 'Could not save changes. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
     <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white">
         <h3 className="font-bold text-lg bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">Edit Company Details</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          {error && (
            <p className="text-red-500 text-sm bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>
          )}
          {EDIT_FIELDS.map((f) => (
            <div key={f.key}>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">{f.label}</label>
              {f.textarea ? (
                <textarea
                  value={form[f.key]}
                  onChange={(e) => set(f.key, e.target.value)}
                  rows={3}
                  className={`w-full border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent ${fieldErrors[f.key] ? 'border-red-400' : 'border-gray-200'}`}
                />
              ) : f.key === 'city' ? (
                <CityAutocompleteInput
                  value={form.city}
                  onChange={(v) => set('city', v)}
                  hasError={!!fieldErrors.city}
                />
              ) : (
                <input
                  type={f.type || 'text'}
                  value={form[f.key]}
                  onChange={(e) => set(f.key, e.target.value)}
                  className={`w-full border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent ${fieldErrors[f.key] ? 'border-red-400' : 'border-gray-200'}`}
                />
              )}
              {fieldErrors[f.key] && <p className="text-red-500 text-xs mt-1">{fieldErrors[f.key]}</p>}
            </div>
          ))}
          <div className="flex gap-2 pt-2 sticky bottom-0 bg-white">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-gray-300 text-gray-600 py-2 rounded-lg text-sm font-semibold hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 bg-gradient-to-r from-[#7C3AED] to-[#EC4899] text-white py-2 rounded-xl text-sm font-semibold hover:opacity-90 disabled:opacity-50"
            >
              {saving ? 'Saving\u2026' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Upload Document (on behalf of employer) form                            */
/* ---------------------------------------------------------------------- */

function UploadDocumentForm({ apiPrefix, employerId, onUploaded }) {
  const [open, setOpen] = useState(false);
  const [docType, setDocType] = useState('gst');
  const [docNumber, setDocNumber] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { setError('Please choose a file to upload.'); return; }
    setUploading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('document_type', docType);
      if (docNumber) fd.append('document_number', docNumber);
      fd.append('file', file);

      await api.post(`${apiPrefix}/employers/${employerId}/documents`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setDocNumber('');
      setFile(null);
      setOpen(false);
      onUploaded();
    } catch (err) {
      setError(err.response?.data?.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="border border-[#7C3AED] text-[#7C3AED] px-4 py-2 rounded-full text-sm font-semibold hover:bg-[#F5F3FF]"
      >
        + Upload Document
      </button>
    );
  }

  return (
   <form onSubmit={submit} className="bg-white border border-gray-100 rounded-2xl shadow-sm p-4 mb-6 space-y-3">
      <div className="flex items-center justify-between">
        <p className="font-semibold text-sm text-[#0D1B3D]">Upload Document on Behalf of Employer</p>
        <button type="button" onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600 text-lg leading-none">&times;</button>
      </div>

      {error && <p className="text-red-500 text-xs bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Document Type</label>
          <select
            value={docType}
            onChange={(e) => setDocType(e.target.value)}
             className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
          >
            {Object.entries(DOC_LABELS).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Document Number (optional)</label>
          <input
            type="text"
            value={docNumber}
            onChange={(e) => setDocNumber(e.target.value)}
           className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">File (PDF, JPG, PNG &mdash; max 5MB)</label>
        <input
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="w-full text-sm"
        />
      </div>

      <button
        type="submit"
        disabled={uploading}
        className="bg-gradient-to-r from-[#7C3AED] to-[#EC4899] text-white px-4 py-2 rounded-xl text-sm font-semibold hover:opacity-90 disabled:opacity-50"
      >
        {uploading ? 'Uploading\u2026' : 'Upload'}
      </button>
    </form>
  );
}

/* ---------------------------------------------------------------------- */
/* Document row (approve/reject/resubmit) — unchanged                      */
/* ---------------------------------------------------------------------- */

function DocumentRow({ doc, apiPrefix, onChanged, allowRedecide = false }) {
  const [remarks, setRemarks] = useState('');
  const [busy, setBusy] = useState(false);
  const [showRemarksFor, setShowRemarksFor] = useState(null); // 'reject' | 'resubmit' | null

  const meta = DOC_STATUS_META[doc.status] || DOC_STATUS_META.pending;
  const fileUrl = resolveMediaUrl(doc.file_path);

  const act = async (action, needsRemarks) => {
    if (needsRemarks && showRemarksFor !== action) {
      setShowRemarksFor(action);
      return;
    }
    setBusy(true);
    try {
      const endpoint =
        action === 'approve' ? `${apiPrefix}/documents/${doc.id}/approve`
        : action === 'reject' ? `${apiPrefix}/documents/${doc.id}/reject`
        : `${apiPrefix}/documents/${doc.id}/request-resubmission`;

      await api.put(endpoint, remarks ? { remarks } : {});
      setShowRemarksFor(null);
      setRemarks('');
      onChanged();
    } catch (err) {
      alert(err.response?.data?.message || 'Action failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
     <div className="border border-gray-100 rounded-xl p-4">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <div>
          <p className="font-semibold text-[#0D1B3D] text-sm">{DOC_LABELS[doc.document_type] || doc.document_type}</p>
          {doc.document_number && <p className="text-xs text-gray-500">No: {doc.document_number}</p>}
        </div>
        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${meta.cls}`}>{meta.label}</span>
      </div>

      {fileUrl && (
       <a href={fileUrl} target="_blank" rel="noreferrer" className="text-[#7C3AED] underline text-xs">
  View / Download document
</a>
      )}

      {doc.remarks && (
        <p className="text-xs text-gray-500 mt-2">Remarks: {doc.remarks}</p>
      )}
      {doc.reviewer && (
        <p className="text-xs text-gray-400 mt-1">
          Reviewed by {doc.reviewer.name} on {fmtDateTime(doc.reviewed_at)}
        </p>
      )}

      {/* Actions are always available, regardless of current status, so a reviewer
          can flip an approved document to rejected or vice versa. */}
      {doc.status !== 'pending' && !allowRedecide ? (
        <p className="text-xs text-gray-400 mt-3 italic">
          Already reviewed. Only an admin can change this.
        </p>
      ) : (
      <div className="mt-3">
        {showRemarksFor && (
          <textarea
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Remarks (required)"
            rows={2}
             className="w-full border rounded-xl px-3 py-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
          />
        )}
        <div className="flex flex-wrap gap-2">
          <button
            disabled={busy}
            onClick={() => act('approve', false)}
            className="bg-emerald-50 text-emerald-600 px-3 py-1.5 rounded-full text-xs font-semibold hover:bg-emerald-100 disabled:opacity-50"
          >
            {doc.status === 'approved' ? 'Re-Approve' : 'Approve'}
          </button>
          <button
            disabled={busy}
            onClick={() => act('reject', true)}
            className="bg-red-50 text-red-600 px-3 py-1.5 rounded-full text-xs font-semibold hover:bg-red-100 disabled:opacity-50"
          >
            {showRemarksFor === 'reject' ? 'Confirm Reject' : doc.status === 'rejected' ? 'Re-Reject' : 'Reject'}
          </button>
          <button
            disabled={busy}
            onClick={() => act('resubmit', true)}
            className="bg-amber-50 text-amber-600 px-3 py-1.5 rounded-full text-xs font-semibold hover:bg-amber-100 disabled:opacity-50"
          >
            {showRemarksFor === 'resubmit' ? 'Confirm Request' : 'Request Resubmission'}
          </button>
        </div>
      </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Main detail view                                                         */
/* ---------------------------------------------------------------------- */

function VerificationPanelDetail({ apiPrefix, listPathPrefix, employerId, onBack, allowRedecide = false }) {
  const params = useParams();
  const id = employerId ?? params.id;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rejectRemarks, setRejectRemarks] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`${apiPrefix}/employers/${id}`);
      setData(res.data);
    } catch (err) {
      console.error('Failed to load employer verification detail', err);
    } finally {
      setLoading(false);
    }
  }, [apiPrefix, id]);

  useEffect(() => { load(); }, [load]);

  const startReview = async () => {
    setBusy(true);
    try {
      await api.put(`${apiPrefix}/employers/${id}/start-review`);
      await load();
    } catch (err) {
      alert(err.response?.data?.message || 'Could not start review.');
    } finally {
      setBusy(false);
    }
  };

  const rejectEmployer = async () => {
    if (!showRejectForm) { setShowRejectForm(true); return; }
    if (!rejectRemarks) return;
    setBusy(true);
    try {
      await api.put(`${apiPrefix}/employers/${id}/reject`, { remarks: rejectRemarks });
      setShowRejectForm(false);
      setRejectRemarks('');
      await load();
    } catch (err) {
      alert(err.response?.data?.message || 'Could not reject employer.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="p-10 text-center text-gray-500">Loading&hellip;</div>;
  if (!data) return <div className="p-10 text-center text-red-500">Not found or not authorized.</div>;

  const { employer, documents, history } = data;
  const meta = STATUS_META[employer.company_verified_status] || STATUS_META.pending_verification;

  return (
    <div className="bg-[#FDF5FF] -m-6 p-6 min-h-screen">
      {onBack ? (
  <button onClick={onBack} className="text-xs text-[#7C3AED] hover:underline">&larr; Back to list</button>
) : (
  <Link to={listPathPrefix} className="text-xs text-[#7C3AED] hover:underline">&larr; Back to list</Link>
)}

      <div className="flex items-center justify-between flex-wrap gap-3 mt-2 mb-1">
        <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">{employer.name}</h2>
        <div className="flex items-center gap-2">
          <span className={`px-3 py-1.5 rounded-full text-sm font-semibold ${meta.cls}`}>{meta.label}</span>
          <button
            onClick={() => setShowEditModal(true)}
            className="border border-gray-300 text-gray-600 px-3 py-1.5 rounded-full text-xs font-semibold hover:border-[#7C3AED] hover:text-[#7C3AED]"
          >
            &#9998; Edit Details
          </button>
        </div>
      </div>
      <p className="text-gray-500 text-sm mb-6">{employer.email} &middot; {employer.phone}</p>

      {/* Employer details */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 mb-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
        <p><span className="text-gray-400">Company GST:</span> {employer.company_gst || '\u2014'}</p>
        <p><span className="text-gray-400">Industry:</span> {employer.company_industry || '\u2014'}</p>
        <p><span className="text-gray-400">Website:</span> {employer.company_website || '\u2014'}</p>
        <p><span className="text-gray-400">City:</span> {employer.city || '\u2014'}</p>
        <p><span className="text-gray-400">Official Email:</span> {employer.company_official_email || '\u2014'}
          {employer.company_email_verified_at && <span className="text-[#10B981] ml-1">(verified {fmtDateTime(employer.company_email_verified_at)})</span>}
        </p>
        <p><span className="text-gray-400">Verified At:</span> {employer.company_verified_at ? fmtDateTime(employer.company_verified_at) : '\u2014'}</p>
      </div>

      {/* Panel-level actions */}
      <div className="flex flex-wrap gap-2 mb-6">
        {employer.company_verified_status === 'documents_submitted' && (
          <button
            disabled={busy}
            onClick={startReview}
            className="bg-gradient-to-r from-purple-600 to-pink-500 text-white px-4 py-2 rounded-xl text-sm font-semibold hover:opacity-90 disabled:opacity-50"
          >
            Start Review
          </button>
        )}
        {employer.company_verified_status !== 'verified' && employer.company_verified_status !== 'rejected' && (
          <div className="flex items-center gap-2">
            {showRejectForm && (
              <input
                type="text"
                value={rejectRemarks}
                onChange={(e) => setRejectRemarks(e.target.value)}
                placeholder="Reason for rejection"
                className="border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
              />
            )}
            <button
              disabled={busy}
              onClick={rejectEmployer}
              className="bg-red-50 text-red-600 px-4 py-2 rounded-xl text-sm font-semibold hover:bg-red-100 disabled:opacity-50"
            >
              {showRejectForm ? 'Confirm Reject Employer' : 'Reject Verification'}
            </button>
          </div>
        )}
      </div>

      {/* Documents */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <h3 className="font-bold text-lg bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">Submitted Documents</h3>
        <UploadDocumentForm apiPrefix={apiPrefix} employerId={id} onUploaded={load} />
      </div>
      {documents.length === 0 ? (
        <p className="text-gray-400 text-sm mb-6">No documents submitted yet.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          {documents.map((doc) => (
            <DocumentRow key={doc.id} doc={doc} apiPrefix={apiPrefix} onChanged={load} allowRedecide={allowRedecide} />
          ))}
        </div>
      )}

      {/* History */}
      <h3 className="font-bold text-lg mb-3 bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">Verification History</h3>
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 divide-y divide-gray-100">
        {history.length === 0 && (
          <p className="text-gray-400 text-sm p-5">No activity yet.</p>
        )}
        {history.map((h) => (
          <div key={h.id} className="p-4 text-sm flex items-start justify-between gap-3">
            <div>
              <p className="font-medium text-[#0D1B3D]">{ACTION_LABELS[h.action] || h.action}</p>
              {h.document && <p className="text-xs text-gray-500">{DOC_LABELS[h.document.document_type] || h.document.document_type}</p>}
              {h.remarks && <p className="text-xs text-gray-500 mt-1">{h.remarks}</p>}
              <p className="text-xs text-gray-400 mt-1">
                By {h.actor ? `${h.actor.name} (${h.actor.role})` : 'System'}
              </p>
            </div>
            <span className="text-xs text-gray-400 whitespace-nowrap">{fmtDateTime(h.created_at)}</span>
          </div>
        ))}
      </div>

      {showEditModal && (
        <EditEmployerModal
          apiPrefix={apiPrefix}
          employer={employer}
          onClose={() => setShowEditModal(false)}
          onSaved={load}
        />
      )}
    </div>
  );
}

export default VerificationPanelDetail;